import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { MapPreview } from "../../google-maps/MapPreview.js";
import { AddressDetails__AddressFragment } from "./Addresses.generated.js";
import { AddressWithBreaks } from "./AddressWithBreaks.js";
import { getAddressDirectionUrl, getAddressSearchUrl } from "./helpers.js";

const SIZE = 60;

const DEFAULT_POINT = { lat: 50.701606, lng: 7.37573 };
const DEFAULT_ZOOM = 4;

const ADDRESS_ZOOM = 13;

interface Props {
  buildingTitle?: string;
  title?: string;
  address: AddressDetails__AddressFragment | null;
  AddButton?: React.ReactElement;
  EditButton?: React.ReactElement;
  BuildingButton?: React.ReactElement;
}

export const AddressBox = ({
  buildingTitle,
  title,
  address,
  AddButton,
  EditButton,
  BuildingButton,
}: Props) => {
  const { t } = useTranslate("AddressBox");

  const loc = address?.location;
  const previewProps = loc
    ? {
        zoom: ADDRESS_ZOOM,
        location: { lat: loc.lat, lng: loc.lng },
        withMarker: true,
      }
    : {
        zoom: DEFAULT_ZOOM,
        location: DEFAULT_POINT,
        isGreyedOut: true,
      };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        minHeight: `${SIZE}px`,
      }}
    >
      <MapPreview
        width={SIZE}
        height={SIZE}
        {...previewProps}
        href={address ? getAddressSearchUrl(address) : undefined}
      />

      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          pl: 1,
          flexGrow: 1,
          flexShrink: 1,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack
          direction="column"
          spacing={0}
          sx={{
            minHeight: `${SIZE}px`,
          }}
        >
          {title ? (
            <Typography variant="caption" component="div" color="textPrimary">
              {title}
            </Typography>
          ) : null}
          {address ? (
            <MuiLink
              target="_blank"
              rel="noreferrer nofollow"
              href={getAddressDirectionUrl(address)}
              variant="body2"
            >
              <AddressWithBreaks
                title={buildingTitle}
                address={address}
                useCountry={false}
              />
            </MuiLink>
          ) : (
            <Typography sx={{ color: "grey.500" }} variant="body2">
              {t("No address")}
            </Typography>
          )}
        </Stack>
        {AddButton || EditButton || BuildingButton ? (
          <Stack direction="row" spacing={0.5}>
            {AddButton}
            {EditButton}
            {BuildingButton}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};
